import { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { debounce } from "@mui/material/utils"
import { Card, Grid, Pagination, Autocomplete, TextField, Icon } from "@mui/material"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import DataTable from "examples/Tables/DataTable"
import MDSnackbar from "components/MDSnackbar"
import MDButton from "components/MDButton"

import * as packageService from "services/packages/package.service"
import returnsPackagesDataTable from "./data/returnsPackagesDataTable"

function PackagesToReturn() {
  const rowsPerPage = 10

  const [returnedPackages, setReturnedPackages] = useState([])
  const [isReturnedDataLoading, setIsReturnedDataLoading] = useState(false)
  const [currentReturnedPage, setCurrentReturnedPage] = useState(1)
  const [returnedPackageTotal, setReturnedPackageTotal] = useState(0)
  const [options, setOptions] = useState([])
  const [selectedReturnedPackageBarcodes, setSelectedReturnedPackageBarcodes] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [loading, setLoading] = useState(false)

  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })

  const selectedBarcodesRef = useRef(selectedReturnedPackageBarcodes)
  useEffect(() => {
    selectedBarcodesRef.current = selectedReturnedPackageBarcodes
  }, [selectedReturnedPackageBarcodes])

  const isScanningRef = useRef(false)

  const handleOpenSnackbar = (status, content, icon) => {
    setSnackbarObject((prev) => ({ ...prev, open: true, content, color: status, icon }))
  }

  const handleCloseSnackbar = () =>
    setSnackbarObject((prev) => ({ ...prev, open: false }))

  // =========================
  // ADD PACKAGE BY BARCODE
  // =========================
  const addPackageByBarcode = useCallback(async (rawBarcode) => {
    if (isScanningRef.current) return
    isScanningRef.current = true

    const barcode = rawBarcode.trim()

    try {
      const results = await packageService.findAllPackagesToReturned(barcode)
      const packageList = results?.packageList || []
      console.log("[scanner] search results for", JSON.stringify(barcode), ":", packageList)

      const found = packageList.find(
        (p) => p.barcode?.trim().toLowerCase() === barcode.toLowerCase()
      )

      if (!found) {
        handleOpenSnackbar("error", "Colis introuvable ou déjà retourné", "error")
        return
      }

      const alreadyAdded = selectedBarcodesRef.current.some(
        (p) => p.packageId === found.packageId
      )

      if (alreadyAdded) {
        handleOpenSnackbar("warning", "Ce colis est déjà dans la liste", "warning")
        return
      }

      setSelectedReturnedPackageBarcodes((prev) => [...prev, found])
      handleOpenSnackbar("success", `Colis ${found.barcode} ajouté`, "check")
    } catch (error) {
      handleOpenSnackbar("error", "Erreur lors de la recherche du colis", "error")
    } finally {
      isScanningRef.current = false
    }
  }, [])

  // =========================
  // GLOBAL SCANNER LISTENER
  // =========================
  useEffect(() => {
    let buffer = ""
    let lastTime = Date.now()
    let flushTimer = null

    const flush = () => {
      const code = buffer.trim()
      buffer = ""
      if (code.length >= 3) {
        addPackageByBarcode(code)
      }
    }

    const handleKeyDown = (e) => {
      const now = Date.now()

      // reset if typing too slow (not scanner)
      if (now - lastTime > 120) {
        buffer = ""
      }
      lastTime = now

      if (e.key === "Enter") {
        clearTimeout(flushTimer)
        flush()
        return
      }

      // ignore modifier keydowns (Shift, Control, Alt, CapsLock...) so they
      // don't get appended as literal text (e.g. "Shift" before a capital letter)
      if (e.key.length === 1) {
        buffer += e.key
        clearTimeout(flushTimer)
        flushTimer = setTimeout(flush, 150)
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      clearTimeout(flushTimer)
    }
  }, [addPackageByBarcode])

  // =========================
  // SEARCH AUTOCOMPLETE
  // =========================
  const fetchPackages = useMemo(
    () =>
      debounce(async (searchText) => {
        if (searchText.length < 3) {
          setOptions([])
          return
        }

        setLoading(true)
        try {
          const results = await packageService.findAllPackagesToReturned(searchText)
          setOptions(results.packageList || [])
        } catch {
          setOptions([])
        } finally {
          setLoading(false)
        }
      }, 400),
    []
  )

  useEffect(() => {
    fetchPackages(inputValue)
  }, [inputValue, fetchPackages])

  // =========================
  // RETURN SELECTED PACKAGES
  // =========================
  const handleReturnSelected = async () => {
    if (selectedReturnedPackageBarcodes.length === 0) return

    setIsReturnedDataLoading(true)

    try {
      const barcodes = selectedReturnedPackageBarcodes.map((p) => p.barcode)

      await packageService.markPackagesAsReturned(barcodes)

      handleOpenSnackbar("success", "Colis retournés avec succès", "check")

      setReturnedPackages((prev) =>
        prev.filter(
          (pkg) =>
            !selectedReturnedPackageBarcodes.some((s) => s.packageId === pkg.packageId)
        )
      )

      setReturnedPackageTotal((prev) => prev - selectedReturnedPackageBarcodes.length)
      setSelectedReturnedPackageBarcodes([])
      setCurrentReturnedPage(1)
    } catch {
      handleOpenSnackbar("error", "Erreur lors du retour des colis", "error")
    } finally {
      setIsReturnedDataLoading(false)
    }
  }

  const pageCount = (total) => Math.ceil(total / rowsPerPage)

  const handleChangeReturnedPagination = (event, value) => {
    setCurrentReturnedPage(value)
  }

  const paginatedReturnedPackages = useMemo(() => {
    const allPackages = [
      ...returnedPackages,
      ...selectedReturnedPackageBarcodes,
    ].filter((v, i, a) => a.findIndex((t) => t.packageId === v.packageId) === i)

    const start = (currentReturnedPage - 1) * rowsPerPage
    const end = start + rowsPerPage

    return allPackages.slice(start, end)
  }, [returnedPackages, selectedReturnedPackageBarcodes, currentReturnedPage])

  useEffect(() => {
    setReturnedPackages(selectedReturnedPackageBarcodes)
    setReturnedPackageTotal(selectedReturnedPackageBarcodes.length)
    setCurrentReturnedPage(1)
  }, [selectedReturnedPackageBarcodes])

  return (
    <DashboardLayout>
      <DashboardNavbar absolute />

      <MDBox mt={6}>
        <MDBox px={2} mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                limitTags={0}
                filterOptions={(x) => x}
                options={options}
                loading={loading}
                value={selectedReturnedPackageBarcodes}
                onInputChange={(e, v) => setInputValue(v)}
                onChange={(e, v) => {
                  const unique = v.filter(
                    (a, i, arr) =>
                      arr.findIndex((t) => t.packageId === a.packageId) === i
                  )
                  setSelectedReturnedPackageBarcodes(unique)
                }}
                getOptionLabel={(o) => `${o.barcode} - ${o.clientName}`}
                isOptionEqualToValue={(o, v) => o.packageId === v.packageId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Rechercher un Colis (${selectedReturnedPackageBarcodes.length})`}
                    variant="standard"
                  />
                )}
              />
            </Grid>
          </Grid>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={9}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                bgColor="info"
                borderRadius="lg"
                display="flex"
                justifyContent="space-between"
              >
                <MDTypography variant="h6" color="white">
                  Colis à retourner ({returnedPackageTotal})
                </MDTypography>

                <MDButton
                  color="white"
                  variant="outlined"
                  disabled={!selectedReturnedPackageBarcodes.length}
                  onClick={handleReturnSelected}
                >
                  Retourner les colis
                </MDButton>
              </MDBox>

              <MDBox pt={3}>
                {returnedPackages.length === 0 ? (
                  <MDBox p={5} textAlign="center">
                    <MDTypography>Aucun colis sélectionné</MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={returnsPackagesDataTable(paginatedReturnedPackages)}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                  />
                )}

                {returnedPackageTotal > rowsPerPage && (
                  <Grid p={3} container justifyContent="flex-end">
                    <Pagination
                      count={pageCount(returnedPackageTotal)}
                      page={currentReturnedPage}
                      onChange={handleChangeReturnedPagination}
                    />
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Card>
              <MDBox pt={3} px={2}>
                <MDTypography variant="h6" mb={2}>
                  Liste de sélection
                </MDTypography>

                <MDBox sx={{ maxHeight: 600, overflowY: "auto" }}>
                  {selectedReturnedPackageBarcodes.map((option) => (
                    <MDBox
                      key={option.packageId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        mb: 1,
                        border: "1px solid #ddd",
                        borderRadius: 2,
                      }}
                    >
                      <MDBox>
                        <MDTypography variant="caption" fontWeight="bold">
                          {option.barcode}
                        </MDTypography>
                        <MDTypography variant="xxs">
                          {option.clientName}
                        </MDTypography>
                      </MDBox>

                      <Icon
                        sx={{ cursor: "pointer", color: "red" }}
                        onClick={() =>
                          setSelectedReturnedPackageBarcodes((prev) =>
                            prev.filter((p) => p.packageId !== option.packageId)
                          )
                        }
                      >
                        cancel
                      </Icon>
                    </MDBox>
                  ))}

                  {!selectedReturnedPackageBarcodes.length && (
                    <MDTypography variant="caption">
                      Aucun colis en attente...
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        open={snackbarObject.open}
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title="Colis"
        content={snackbarObject.content}
        close={handleCloseSnackbar}
      />
    </DashboardLayout>
  )
}

export default PackagesToReturn